<template>
	<main id="paxExpress" class="pax_global">
		<HeaderTab :title="$t('pax.simulation_titre')" :icon="['fal', 'horse']" />

		<div id="paxSimulation" class="container-fluid">
			<div class="row justify-content-center">
				<div class="col-12 col-lg-8">
					<div class="paragraphes mt-lg-4 mb-4">{{ $t('pax.text_simulation_infos') }}</div>

					<form @submit.prevent="submitPrint">
						<div class="row">
							<div class="col-12 col-md-6">
								<div class="form-group">
									<label>{{ $t('monte.jument') }}<sup>*</sup></label>
									<e-select
										v-model="mare"
										track-by="horse_id"
										label="horse_nom"
										:custom-label="mareLabel"
										:placeholder="$t('monte.selectionnez_jument')"
										:selectedLabel="$t('select.selectedLabel')"
										:options="mares"
										:searchable="true"
										:allow-empty="false"
										:sortable="false"
										:loading="isLoading"
										:show-labels="false"
									>
										<template slot="noOptions">{{ $t('global.list_empty') }}</template>
									</e-select>
								</div>
							</div>
							<div class="col-12 col-md-6">
								<div class="form-group">
									<label>{{ $t('monte.etalon') }}<sup>*</sup></label>
									<e-select
										v-model="stallion"
										track-by="horse_id"
										label="horse_nom"
										:placeholder="$t('monte.selectionnez_etalon')"
										:selectedLabel="$t('select.selectedLabel')"
										:options="stallions"
										:searchable="true"
										:allow-empty="false"
										:sortable="false"
										:loading="isLoading"
										:show-labels="false"
									>
										<template slot="noOptions">{{ $t('global.list_empty') }}</template>
									</e-select>
								</div>
							</div>
						</div>

						<div class="alert alert-warning" v-if="caracterisation_incomplete">
							{{ $t('pax.simulation_caracterisation_incomplete') }}
							<router-link :to="{ name: 'HorsePax', params: { horse_id: mare.horse_id } }">{{ $t('pax.completer_caracterisation') }}</router-link>
						</div>

						<div class="text-center mt-4">
							<b-button type="submit" variant="primary" class="rounded-pill" :disabled="!ready || processing_print">
								{{ $t('pax.simulation_croisement') }}
								<font-awesome-icon v-if="processing_print" :icon="['fas', 'spinner']" pulse />
								<font-awesome-icon v-else :icon="['fal', 'print']" />
							</b-button>
						</div>
					</form>
				</div>
			</div>
		</div>
	</main>
</template>


<script type="text/javascript">
	import HorseMixin from '@/mixins/Horse.js'
	import PaxMixin from '@/mixins/Pax.js'
	import Tools from '@/mixins/Tools.js'

	export default {
		name: "PaxSimulation",
		mixins: [HorseMixin, PaxMixin, Tools],
		data () {
			return {
				mares: [],
				mares_percents: {},
				stallions: [],
				mare: null,
				stallion: null,
				isLoading: true,
				processing_print: false
			}
		},

		created() {
			this.init_component()
		},

		methods: {
			/**
			 * Charge les juments de la licence, le catalogue d'étalons et les taux de
			 * caractérisation PAX, puis trie les deux listes par ordre alphabétique.
			 */
			async init_component() {
				const [mares, stallions, percents] = await Promise.all([
					this.getHorses('F'),
					this.getStallions(),
					this.getCaracterisationPercents()
				])

				this.mares = mares.sort((a, b) => a.horse_nom.localeCompare(b.horse_nom))
				// getStallions et getCaracterisationPercents renvoient null lorsque l'appel échoue
				this.stallions = stallions !== null ? stallions.sort((a, b) => a.horse_nom.localeCompare(b.horse_nom)) : []
				this.mares_percents = percents !== null ? percents : {}

				this.isLoading = false
			},

			/**
			 * Libellé d'une jument dans la liste : son nom, suivi de son taux de caractérisation PAX.
			 * @param {Object} mare Jument à libeller
			 * @return {String}
			 */
			mareLabel(mare) {
				const percent = this.mares_percents[mare.horse_id]
				return percent !== undefined ? `${mare.horse_nom} (${percent}%)` : mare.horse_nom
			},

			/**
			 * Génère et télécharge le PDF de simulation de croisement pour le couple sélectionné.
			 */
			async submitPrint() {
				this.processing_print = true
				try {
					await this.printSimulationCroisement(this.mare, this.stallion)
				}
				finally {
					// l'API rejette si la jument n'appartient pas au client : le spinner doit s'arrêter
					this.processing_print = false
				}
			}
		},

		computed: {
			/**
			 * Une jument et un étalon doivent être sélectionnés pour lancer l'impression.
			 * @return {Boolean}
			 */
			ready() {
				return this.mare !== null && this.stallion !== null
			},

			/**
			 * Une caractérisation PAX partielle donne une simulation moins pertinente :
			 * on invite alors le client à la compléter.
			 * @return {Boolean}
			 */
			caracterisation_incomplete() {
				if(this.mare === null) {
					return false
				}

				const percent = this.mares_percents[this.mare.horse_id]
				return percent === undefined || percent < 100
			}
		},

		components: {
			HeaderTab: () => import('@/components/HeaderTab')
		}
	};
</script>
